import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Popconfirm,
  message,
  Alert,
} from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, CloudDownloadOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { PERMISSIONS } from '../../config/permissions';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import productService from '../../services/productService';
import pricingGroupService from '../../services/pricingGroupService';
import productPricingTiersService from '../../services/productPricingTiersService';

export default function ProductPricingTiersPage() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canCreate = isSuperAdmin || hasPermission(PERMISSIONS.PRICING_SPECIAL_TIERS_CREATE);
  const canEdit = isSuperAdmin || hasPermission(PERMISSIONS.PRICING_SPECIAL_TIERS_EDIT);

  const [products, setProducts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [productId, setProductId] = useState(undefined);
  const [pricingGroupId, setPricingGroupId] = useState(undefined);
  const [tiers, setTiers] = useState([]);
  const [tiersQtyError, setTiersQtyError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [form] = Form.useForm();
  const isEdit = Boolean(editing);

  const maxOrder = tiers.length ? Math.max(...tiers.map((s) => Number(s.tier_order) || 0)) : 0;

  const loadOptions = useCallback(async () => {
    setOptionsLoading(true);
    try {
      const [pRes, gList] = await Promise.all([productService.getProducts({ limit: 500 }), pricingGroupService.list()]);

      const raw = pRes?.data?.products;
      setProducts(Array.isArray(raw) ? raw : []);

      setGroups(Array.isArray(gList) ? gList : []);
    } catch (e) {
      message.error(e?.message || 'Failed to load products or groups');
      setProducts([]);
      setGroups([]);
    } finally {
      setOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const loadTiers = useCallback(async () => {
    if (!productId || !pricingGroupId) {
      setTiers([]);
      setTiersQtyError(null);
      return;
    }
    setLoading(true);
    setTiersQtyError(null);
    try {
      const { tiers: list, tiersQtyValid, tiersQtyError: err } = await productPricingTiersService.list(
        productId,
        pricingGroupId,
      );
      setTiers(Array.isArray(list) ? list : []);
      if (tiersQtyValid === false && err) {
        setTiersQtyError(err);
      } else {
        setTiersQtyError(null);
      }
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Failed to load tiers');
      setTiers([]);
      setTiersQtyError(null);
    } finally {
      setLoading(false);
    }
  }, [productId, pricingGroupId]);

  useEffect(() => {
    loadTiers();
  }, [loadTiers]);

  const onSyncFromSlabs = async () => {
    if (!productId || !pricingGroupId) return;
    setSyncing(true);
    try {
      const res = await productPricingTiersService.syncFromSlabs({ product_id: productId, pricing_group_id: pricingGroupId });
      if (res?.success === false) {
        message.error(res?.message || 'Sync failed');
        return;
      }
      message.success('Tiers synced from slab matrix (requires product base price set).');
      loadTiers();
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      tier_order: maxOrder + 1 || 1,
      product_id: productId,
      pricing_group_id: pricingGroupId,
    });
    setDrawerOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      tier_order: record.tier_order,
      min_qty: record.min_qty,
      max_qty: record.max_qty,
      pricing_per_unit: record.pricing_per_unit,
      min_price: record.min_price,
      max_price: record.max_price,
    });
    setDrawerOpen(true);
  };

  const onClose = () => {
    setDrawerOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      if (isEdit) {
        const res = await productPricingTiersService.update(editing.id, {
          tier_order: values.tier_order,
          min_qty: values.min_qty,
          max_qty: values.max_qty === undefined || values.max_qty === '' ? null : values.max_qty,
          pricing_per_unit: values.pricing_per_unit,
          min_price: values.min_price,
          max_price: values.max_price === undefined || values.max_price === '' ? null : values.max_price,
        });
        if (res?.success === false) {
          message.error(res?.message || 'Update failed');
          return;
        }
        message.success('Tier updated');
      } else {
        const res = await productPricingTiersService.create({
          product_id: values.product_id,
          pricing_group_id: values.pricing_group_id,
          tier_order: values.tier_order,
          min_qty: values.min_qty,
          max_qty: values.max_qty === undefined || values.max_qty === '' ? null : values.max_qty,
          pricing_per_unit: values.pricing_per_unit,
          min_price: values.min_price,
          max_price: values.max_price === undefined || values.max_price === '' ? null : values.max_price,
        });
        if (res?.success === false) {
          message.error(res?.message || 'Create failed');
          return;
        }
        message.success('Tier created');
        setProductId(values.product_id);
        setPricingGroupId(values.pricing_group_id);
      }
      onClose();
      loadTiers();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (record) => {
    try {
      const res = await productPricingTiersService.remove(record.id);
      if (res?.success === false) {
        message.error(res?.message || 'Delete failed');
        return;
      }
      message.success('Tier deleted');
      loadTiers();
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Delete failed');
    }
  };

  const productOptions = products.map((p) => ({
    value: p.id,
    label: p.name || p.slug || p.id,
  }));

  const groupOptions = groups.map((g) => ({
    value: g.id,
    label: g.group_name || g.id,
  }));

  const formatQty = (v) => String(Math.round(Number(v)));

  const columns = [
    { title: 'Order', dataIndex: 'tier_order', key: 'tier_order', width: 80 },
    { title: 'Min qty', dataIndex: 'min_qty', key: 'min_qty', render: (v) => formatQty(v) },
    {
      title: 'Max qty',
      dataIndex: 'max_qty',
      key: 'max_qty',
      render: (v) => (v == null || v === '' ? '— (open)' : formatQty(v)),
    },
    { title: 'Price / unit', dataIndex: 'pricing_per_unit', key: 'pricing_per_unit' },
    { title: 'Min price', dataIndex: 'min_price', key: 'min_price' },
    {
      title: 'Max price',
      dataIndex: 'max_price',
      key: 'max_price',
      render: (v) => (v == null || v === '' ? '— (open)' : v),
    },
    {
      title: 'Actions',
      key: 'act',
      width: 200,
      render: (_, r) => (
        <Space>
          {canEdit && (
            <Button type="default" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
              Edit
            </Button>
          )}
          {canEdit && (
            <Popconfirm
              title="Delete this tier?"
              onConfirm={() => onDelete(r)}
              okText="Delete"
              okButtonProps={{ danger: true }}
            >
              <Button type="text" danger size="small" icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <DashboardPageWrapper>
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Pricing tiers</h1>
     
      </div>

      {tiersQtyError && (
        <Alert type="error" showIcon className="mb-4" message="Quantity range validation" description={tiersQtyError} />
      )}

      <div className="flex flex-wrap gap-4 items-end mb-4 bg-white p-4 rounded-2xl border border-gray-100">
        <div className="min-w-[200px] flex-1">
          <div className="text-xs text-gray-500 mb-1">Product</div>
          <Select
            showSearch
            placeholder="Select product"
            optionFilterProp="label"
            className="w-full"
            loading={optionsLoading}
            value={productId}
            onChange={setProductId}
            options={productOptions}
            allowClear
          />
        </div>
        <div className="min-w-[200px] flex-1">
          <div className="text-xs text-gray-500 mb-1">Pricing group</div>
          <Select
            showSearch
            placeholder="Select group"
            optionFilterProp="label"
            className="w-full"
            loading={optionsLoading}
            value={pricingGroupId}
            onChange={setPricingGroupId}
            options={groupOptions}
            allowClear
          />
        </div>
        {canEdit && productId && pricingGroupId && (
          <Button
            type="default"
            icon={<CloudDownloadOutlined />}
            onClick={onSyncFromSlabs}
            loading={syncing}
            className="!border-emerald-600 !text-emerald-700"
          >
            Sync from slab matrix
          </Button>
        )}
        {canCreate && productId && pricingGroupId && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} className="!bg-emerald-600 !border-emerald-600">
            Add tier
          </Button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-0 overflow-hidden">
        <Table
          rowKey={(r) => r.id}
          columns={columns}
          dataSource={tiers}
          loading={loading}
          pagination={false}
          locale={{
            emptyText: productId && pricingGroupId ? 'No tiers. Sync from slabs or add a tier.' : 'Select product and pricing group.',
          }}
        />
      </div>

      <Drawer
        title={isEdit ? 'Edit tier' : 'New tier'}
        width={420}
        open={drawerOpen}
        onClose={onClose}
        extra={
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" loading={submitting} onClick={onSubmit} className="!bg-emerald-600 !border-emerald-600">
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          {!isEdit && (
            <>
              <Form.Item name="product_id" label="Product" rules={[{ required: true, message: 'Required' }]}>
                <Select showSearch options={productOptions} optionFilterProp="label" placeholder="Product" />
              </Form.Item>
              <Form.Item
                name="pricing_group_id"
                label="Pricing group"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Select showSearch options={groupOptions} optionFilterProp="label" placeholder="Group" />
              </Form.Item>
            </>
          )}
          <Form.Item name="tier_order" label="Tier order" rules={[{ required: true, message: 'Required' }]}>
            <Input type="number" min={1} step={1} />
          </Form.Item>
          <Form.Item name="min_qty" label="Min quantity" rules={[{ required: true, message: 'Required' }]}>
            <InputNumber className="w-full" min={1} precision={0} step={1} />
          </Form.Item>
          <Form.Item name="max_qty" label="Max quantity (empty = open)">
            <InputNumber className="w-full" min={1} precision={0} step={1} allowClear />
          </Form.Item>
          <Form.Item name="pricing_per_unit" label="Price per unit" rules={[{ required: true, message: 'Required' }]}>
            <InputNumber className="w-full" min={0} precision={2} step={0.01} />
          </Form.Item>
          <Form.Item name="min_price" label="Min price" rules={[{ required: true, message: 'Required' }]}>
            <InputNumber className="w-full" min={0} precision={2} step={0.01} />
          </Form.Item>
          <Form.Item name="max_price" label="Max price (empty = open)">
            <InputNumber className="w-full" min={0} precision={2} step={0.01} />
          </Form.Item>
        </Form>
      </Drawer>
    </DashboardPageWrapper>
  );
}
